/* eslint-disable css-modules/no-unused-class  */
import { FC, useEffect, useState } from 'react';
import { translate } from 'translations';
import { useSelector } from 'react-redux';
import {
    selectToken,
    selectIsLeanUser,
    selectIsCxUniversity,
    selectLabsAccess,
    selectIsTournamentsGamePackageEnabled
} from '@redux/slices/userSlice';
import { useGeyHomepageBanners, useLeanContent, useMyCompletedContent, useRecentlyAdded } from '@queries/core/homepage';
import { ContentCardProps, Loader } from 'learning-panel-components';
import { LearningPathBannerProps as LearningPathBanner } from '@components/learningPath/learningPathBanner/learningPathBanner';
import { coreService, tournamentServer } from 'network-utils';
import { useQueryClient } from 'react-query';
import CardsGalleryContainer from '@components/cardsGallery/cardsGalleryContainer';
import convertCourseToContentCard from '@services/convertCourseToContentCard';
import convertSkillTreeToContentCard from '@services/convertSkillTreeToContentCard';
import convertLessonToContentCard from '@services/convertLessonToContentCard';
import convertLearningPathToContentCard from '@services/convertLearningPathToContentCard';
import convertVideosToContentCard from '@services/convertVideosToContentCard';
import HomePageBanners from '@components/assessments/homePageBanners/homePageBanners';
import convertCourseToBanner from '@services/convertCourseToBanner';
import convertLessonToBanner from '@services/convertLessonToBanner';
import convertSkillTreeToBanner from '@services/convertSkillTreeToBanner';
import convertLearningPathToBanner from '@services/convertLearningPathToBanner';
import { SkillTreeBannerProps } from '@components/skillTree/skillTreeBanner/skillTreeBanner';
import { getUpcomingTournaments, useGetUpcomingTournaments } from '@queries/tournaments/tournament';
import { getCoreUpcomingTournaments, useGetCoreUpcomingTournaments } from '@queries/core/tournaments';
import { AssessmentBannerProps as AssessmentBanner } from '@components/assessments/banner/assessmentBanner';
import { LabBannerProps } from '@components/learn/labBanner/labBanner';
import { useGetUncompletedAssessments } from '@queries/platform/assessments';
import convertVideosToBanner from '@services/convertVideosToBanner';
import eventBus from '@services/eventBus';
import { CourseBannerProps } from '@components/learn/courseBanner/courseBannerTypes';
import { LessonBannerProps } from '@components/learn/lessonBanner/lessonBanner';
import convertLabToBanner from '@services/convertLabToBanner';
import styles from './all.module.scss';

const AllContainer: FC<AllProps> = (props: AllProps) => {
    const { setCertificateCourseUuid, setIsCustomCourse, setVideoTutorialUuid, onContactUsClick, fullWidth } = props;
    const token = useSelector(selectToken);
    const isTournamentPackageEnabled = useSelector(selectIsTournamentsGamePackageEnabled);
    const queryClient = useQueryClient();

    const isLeanUser = useSelector(selectIsLeanUser);
    const [myCompletedContent, setMyCompletedContent] = useState<ContentCardProps[]>([]);
    const [isCxUniversity, setIsCxUniversity] = useState<boolean | undefined>(undefined);
    const [leanContent, setLeanContent] = useState<ContentCardProps[]>([]);
    const [recentlyAdded, setRecentlyAdded] = useState<ContentCardProps[]>([]);
    const [upComingTournaments, setUpComingTournaments] = useState<tournamentServer.UpComingTournament[]>([]);
    const [uncompletedAssessments, setUncompletedAssessments] = useState<AssessmentBanner[]>([]);
    const [learningPathsBanner, setLearningPathsBanner] = useState<LearningPathBanner[]>([]);
    const [skillTreeBanner, setSkillTreesBanner] = useState<SkillTreeBannerProps[]>([]);
    const [labBanners, setLabBanners] = useState<LabBannerProps[]>([]);
    const [lessonsBanner, setLessonsBanner] = useState<LessonBannerProps[]>([]);
    const [courseBanner, setCourseBanner] = useState<CourseBannerProps[]>([]);
    const { data: upcomingTournamentsData } = useGetUpcomingTournaments(token, isTournamentPackageEnabled);
    const { data: upcomingCoreTournamentsData } = useGetCoreUpcomingTournaments(token, isTournamentPackageEnabled);
    const { data: myCompletedContentData, isLoading: myCompletedContentLoading } = useMyCompletedContent(token);
    const { data: recentlyAddedData, isLoading: recentlyAddedLoading } = useRecentlyAdded(token);
    const { data: uncompletedAssessmentsData } = useGetUncompletedAssessments(!isLeanUser);
    const { data: bannersData, isLoading: bannersIsLoading } = useGeyHomepageBanners(token);
    const { data: leanContentData } = useLeanContent(token, isCxUniversity);
    const labsAccess = useSelector(selectLabsAccess);

    const isUniversity = useSelector(selectIsCxUniversity);
    if (isUniversity && !isCxUniversity) setIsCxUniversity(true);

    useEffect(() => {
        if (!bannersData || bannersData.success === false) return;
        const tempLessons: LessonBannerProps[] = [];
        const tempCourses: CourseBannerProps[] = [];
        const tempLearningPaths: LearningPathBanner[] = [];
        const tempSkillTree: SkillTreeBannerProps[] = [];
        const tempLabs: LabBannerProps[] = [];

        bannersData.items.forEach((item: any) => {
            switch (item.contentType) {
                case 'lesson':
                    tempLessons.push(convertLessonToBanner(item));
                    break;
                case 'course':
                    tempCourses.push(convertCourseToBanner(item));
                    break;
                case 'skillTree':
                    tempSkillTree.push(convertSkillTreeToBanner(item));
                    break;
                case 'learningPath':
                    tempLearningPaths.push(convertLearningPathToBanner(item));
                    break;
            }
        });
        if (bannersData.lab && labsAccess) {
            tempLabs.push(...bannersData.lab.map((lab: coreService.LabContentCard) => convertLabToBanner(lab)));
        }

        setLessonsBanner(tempLessons);
        setCourseBanner(tempCourses);
        setLearningPathsBanner(tempLearningPaths);
        setSkillTreesBanner(tempSkillTree);
        setLabBanners(tempLabs);
        console.log('tempLabs', tempLabs, tempLessons, tempCourses);
    }, [bannersData, isLeanUser]);

    useEffect(() => {
        if (!myCompletedContentData) return;
        if ((myCompletedContentData as any).success === false) return;

        const tempCourses = myCompletedContentData.courses.map((course: any) =>
            convertCourseToContentCard(course, isLeanUser, setCertificateCourseUuid, setIsCustomCourse)
        );

        const tempSkillTrees = myCompletedContentData.skillTrees.map((skillTree: any) =>
            convertSkillTreeToContentCard(skillTree)
        );

        const tempLearningPaths = myCompletedContentData.learningPaths.map((learningPath: any) =>
            convertLearningPathToContentCard(learningPath)
        );

        const tempVideos = myCompletedContentData.videoTutorials.map((video: coreService.VideoTutorialData) =>
            convertVideosToContentCard(video, setVideoTutorialUuid)
        );

        setMyCompletedContent([...tempSkillTrees, ...tempCourses, ...tempLearningPaths, ...tempVideos]);
    }, [myCompletedContentData, isLeanUser]);

    useEffect(() => {
        if (!leanContentData) return;
        if ((leanContentData as any).success === false) return;

        const tempCourses = leanContentData.courses?.map((course: any) =>
            convertCourseToContentCard(course, isLeanUser, setCertificateCourseUuid, setIsCustomCourse)
        );

        const tempLessons = leanContentData.lessons?.map((lesson: any) =>
            convertLessonToContentCard(lesson, isLeanUser, onContactUsClick)
        );

        const tempLearningPaths = leanContentData.learningPaths?.map((learningPath: any) =>
            convertLearningPathToContentCard(learningPath)
        );

        setLeanContent([...tempLearningPaths, ...tempCourses, ...tempLessons]);
    }, [leanContentData]);

    useEffect(() => {
        if (!recentlyAddedData) return;
        if ((recentlyAddedData as any).success === false) return;

        const tempCourses = recentlyAddedData?.courses
            .filter((course: coreService.Course) => course.completionPercentage !== 100)
            .map(course => {
                const contentCard = convertCourseToContentCard(
                    course,
                    isLeanUser,
                    setCertificateCourseUuid,
                    setIsCustomCourse
                );
                return { contentCard };
            });

        const tempLessons = recentlyAddedData.lessons
            ?.filter((lesson: coreService.Lesson) => lesson.status !== 'completed')
            .map(lesson => {
                const contentCard = convertLessonToContentCard(lesson, isLeanUser, onContactUsClick);
                const banner = convertLessonToBanner(lesson);
                return { banner, contentCard };
            });

        const tempSkillTree = recentlyAddedData.skillTrees
            ?.filter((skillTree: coreService.SkillTree) => skillTree.progress?.status !== 'completed')
            .map(skillTree => {
                const contentCard = convertSkillTreeToContentCard(skillTree);
                const banner = convertSkillTreeToBanner(skillTree);
                return { banner, contentCard };
            });

        const tempLearningPaths = recentlyAddedData.learningPaths
            ?.filter((learningPath: coreService.LearningPath) => learningPath.progress?.status !== 'completed')
            .map(learningPath => {
                const contentCard = convertLearningPathToContentCard(learningPath);
                const banner = convertLearningPathToBanner(learningPath);
                return { banner, contentCard };
            });

        const tempVideo = recentlyAddedData.videoTutorials
            ?.filter((video: coreService.VideoTutorialData) => video.status !== 'completed')
            .map(video => {
                const contentCard = convertVideosToContentCard(video, setVideoTutorialUuid);
                const banner = convertVideosToBanner(video, setVideoTutorialUuid);
                return { banner, contentCard };
            });

        setRecentlyAdded([
            ...tempCourses?.map(course => course.contentCard),
            ...tempLessons?.map(lesson => lesson.contentCard),
            ...tempLearningPaths?.map(learningPath => learningPath.contentCard),
            ...tempSkillTree?.map(skillTree => skillTree.contentCard),
            ...tempVideo?.map(video => video.contentCard)
        ]);
    }, [recentlyAddedData, isLeanUser]);

    useEffect(() => {
        if (upcomingTournamentsData && upcomingTournamentsData.success && !isTournamentPackageEnabled) {
            setUpComingTournaments(upcomingTournamentsData.tournaments.filter((tournament: any) => tournament.canJoin));
        } else if (upcomingCoreTournamentsData && upcomingCoreTournamentsData.success && isTournamentPackageEnabled) {
            setUpComingTournaments(
                upcomingCoreTournamentsData?.tournaments?.filter((tournament: any) => tournament.canJoin) || []
            );
        }
    }, [upcomingTournamentsData, upcomingCoreTournamentsData, isTournamentPackageEnabled]);

    useEffect(() => {
        if (!uncompletedAssessmentsData?.assessments) return;

        const uncompletedAssessmentsTmp: AssessmentBanner[] = uncompletedAssessmentsData.assessments.map(
            (assessment: any) => {
                return {
                    uuid: assessment.uuid,
                    name: assessment.name,
                    url: assessment.url,
                    categories: assessment.categories,
                    fromDate: assessment.start_date,
                    toDate: assessment.end_date,
                    isStarted: assessment.is_started
                };
            }
        );
        setUncompletedAssessments(uncompletedAssessmentsTmp);
    }, [uncompletedAssessmentsData]);

    useEffect(() => {
        const callback = (data: { type: string }) => {
            if (['reconnected', 'openTournamentChanged'].includes(data.type)) {
                queryClient.invalidateQueries([getUpcomingTournaments, token]);
                queryClient.invalidateQueries([getCoreUpcomingTournaments, token]);
            }
        };

        eventBus.on('notification', callback);

        return () => {
            eventBus.remove('notification', callback);
        };
    }, [queryClient, token]);

    return (
        <>
            <div id="all-content-page" className={styles['all-content-wrapper']}>
                {myCompletedContentLoading || recentlyAddedLoading ? (
                    <Loader showBackground={false} />
                ) : (
                    <>
                        <HomePageBanners
                            assessments={uncompletedAssessments}
                            upComingTournaments={upComingTournaments}
                            learningPaths={learningPathsBanner}
                            skillTreeBanner={skillTreeBanner}
                            videoTutorialsBanner={[]}
                            showDivider={false}
                            courseBanner={courseBanner}
                            lessonsBanner={lessonsBanner}
                            labBanner={labBanners}
                        />

                        <div className={fullWidth ? styles.container : styles['no-full-container']}>
                            {recentlyAdded.length > 0 && (
                                <CardsGalleryContainer
                                    rows={2}
                                    columns={2}
                                    noFullWidthColumn={3}
                                    data={recentlyAdded}
                                    title={translate('recentlyAdded')}
                                    infoDescription={translate('ContentWe’veAddedLately')}
                                    fullWidth={fullWidth}
                                />
                            )}
                            {recentlyAdded.length > 0 && <div className={styles.space}></div>}
                            {myCompletedContent.length > 0 && (
                                <CardsGalleryContainer
                                    rows={2}
                                    columns={2}
                                    noFullWidthColumn={3}
                                    data={myCompletedContent}
                                    title={translate('MyCompletedContent')}
                                    fullWidth={fullWidth}
                                    infoDescription={translate('CoursesAndOtherContentYouCompletedAndYourCertificates')}
                                />
                            )}

                            {myCompletedContent.length === 0 && (
                                <div className={styles['empty-state']}>
                                    <div className={styles.text}>
                                        <div className={styles['title-no-assigned']}>{translate('noCertificates')}</div>
                                        <div className={styles.description}>{translate('noCertificatesDesc')} 💪🏻</div>
                                    </div>
                                    <img
                                        className={styles.img}
                                        src={`${process.env.PUBLIC_URL}/images/completedCourseEmpty.svg`}
                                        alt="noAssignedContent"
                                    ></img>
                                </div>
                            )}
                        </div>
                        {leanContent.length && (
                            <div className={styles.container}>
                                <CardsGalleryContainer
                                    rows={2}
                                    columns={4}
                                    noFullWidthColumn={3}
                                    data={leanContent}
                                    title={translate('ATasteOfCodebashing')}
                                    fullWidth={fullWidth}
                                />
                            </div>
                        )}
                    </>
                )}
            </div>
        </>
    );
};

interface AllProps {
    setCertificateCourseUuid: Function;
    setIsCustomCourse: Function;
    onContactUsClick: Function;
    setVideoTutorialUuid: Function;
    fullWidth?: boolean;
}

export default AllContainer;
